import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getUsageSummary } from "../lib/api/usage";
import type { UsageSummary } from "../types/usage";

interface UsageContextType {
  usage: UsageSummary | null;
  isLoading: boolean;
  error: string | null;
  hasSubscription: boolean;
  refreshUsage: () => Promise<void>;
  updateRemainingFromResponse: (remaining: number) => void;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export function UsageProvider({ children }: { children: ReactNode }) {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(true);
  const { clerkUser } = useAuth();

  const refreshUsage = useCallback(async () => {
    if (!clerkUser) return;
    try {
      setIsLoading(true);
      setError(null);
      const summary = await getUsageSummary();
      setUsage(summary);
      setHasSubscription(true);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHasSubscription(false);
        setUsage(null);
      } else {
        setError(err?.message || "Failed to load usage");
      }
    } finally {
      setIsLoading(false);
    }
  }, [clerkUser]);

  const updateRemainingFromResponse = useCallback((remaining: number) => {
    setUsage((prev) => (prev ? { ...prev, remaining, used: prev.limit - remaining } : prev));
  }, []);

  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  return (
    <UsageContext.Provider value={{ usage, isLoading, error, hasSubscription, refreshUsage, updateRemainingFromResponse }}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage(): UsageContextType {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error("useUsage must be used within a UsageProvider");
  }
  return context;
}
